import { Fragment, useMemo } from "react";
import { Skeleton } from "@material-ui/lab";
import { DataTableV2, Text } from "components/commons";
import locale from "localization";
import styles from "./delivery-log-form/delivery-log.module.scss";
import {
  prettifyDeliveryRecipient,
  prettifyEmployeePosition,
  prettifyProductType,
} from "utils/pretty.utils";
import { dateFormat, dateTimeFormatV2 } from "utils";
import useDataTable from "hooks/useDataTable";
import { fuelColumns, nonFuelColumns } from "./delivery-log-form/products/columns";
import {
  mapDataToListViewFuel,
  mapDataToListViewNonFuel,
} from "./delivery-log-form/products/mapper";
import useProducts from "hooks/useProducts";
import { useMount } from "hooks";
import moment from "moment";
import { ProductCodeEnum } from "enums";

const DisplayText = ({ value, formatter, width = "300px", loading }) => {
  if (loading) return <Skeleton width={width} height="30px" />;
  return <>{formatter ? formatter(value) : value}</>;
};

const ViewRow = ({ label, value, formatter, loading }) => (
  <div className={styles.viewRow}>
    <Text strong color="black">
      {label}
    </Text>
    <Text color="black">
      <DisplayText value={value} formatter={formatter} loading={loading} />
    </Text>
  </div>
);

const ViewDeliveryLog = ({ deliveryLog, loading }) => {
  const formatDateTime = (start, end) => {
    if (!start && !end) return "--";
    return `${dateTimeFormatV2(start)} ${end ? "-" : ""} ${dateTimeFormatV2(end)}`;
  };

  const { allProducts, fetch: getAllProducts } = useProducts({ withStationCode: true });

  useMount(async () => {
    await getAllProducts();
  });

  const isFuelOnly = deliveryLog?.productType === "fuel";

  const fuelProductCodes = useMemo(
    () => new Set([ProductCodeEnum.DIESEL, ProductCodeEnum.GAS91, ProductCodeEnum.GAS95, ProductCodeEnum.GAS97]),
    []
  );

  const filteredProducts = useMemo(() => {
    const products = deliveryLog?.products || [];
    const typeFiltered = products.filter((p) =>
      isFuelOnly ? fuelProductCodes.has(p.product) : !fuelProductCodes.has(p.product)
    );
    const seen = new Set();
    return typeFiltered.filter((p) => {
      if (seen.has(p.product)) return false;
      seen.add(p.product);
      return true;
    });
  }, [deliveryLog?.products, isFuelOnly, fuelProductCodes]);

  const uniqueWitnesses = useMemo(() => {
    const witnesses = deliveryLog?.witnesses || [];
    const seen = new Set();
    return witnesses.filter(({ name, position }) => {
      const key = `${name}|${position}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [deliveryLog?.witnesses]);

  const { table } = useDataTable({
    value: filteredProducts,
    filter: { initialState: {}, mapper: (param) => param },
    table: {
      key: "products",
      mapper: (products) => {
        if (isFuelOnly) {
          return mapDataToListViewFuel({
            products,
            allProducts,
          });
        }

        return mapDataToListViewNonFuel({
          products,
          allProducts,
        });
      },
      columns: isFuelOnly
        ? fuelColumns.filter((c) => c.key !== "actions")
        : nonFuelColumns.filter((c) => c.key !== "actions"),
    },
  });

  return (
    <>
      <div className={styles.viewForm}>
        <Text strong color="black" className={styles.sectionTitle}>
          {locale.deliveryInformation}
        </Text>

        <ViewRow
          label={locale.deliveryRecipient}
          value={prettifyDeliveryRecipient(deliveryLog?.recipient)}
          loading={loading}
        />

        <ViewRow
          label={locale.shift}
          loading={loading}
          value={formatDateTime(deliveryLog?.shift?.startShift, deliveryLog?.shift?.endShift)}
        />

        <ViewRow
          label={locale.deliveryDate}
          value={deliveryLog?.deliveredAt}
          formatter={(date) => dateFormat(date)}
          loading={loading}
        />

        <ViewRow
          label={locale.deliveryTime}
          value={deliveryLog?.deliveredAt}
          formatter={(date) => moment(date).format("hh:mm A")}
          loading={loading}
        />

        <ViewRow
          label={locale.poReferenceNumber}
          value={deliveryLog?.poReferenceNumber}
          loading={loading}
        />
        <ViewRow label={locale.plateNumber} value={deliveryLog?.plateNumber} loading={loading} />

        <br />
        <br />

        <Text strong color="black" className={styles.sectionTitle}>
          {locale.witnessInformation}
        </Text>

        <div className={styles.viewWitness}>
          {uniqueWitnesses.map(({ name, position }, index) => (
            <Fragment key={index}>
              {index === 0 && (
                <>
                  <Text strong color="black">
                    {locale.witness}
                  </Text>
                  <Text strong color="black">
                    {locale.position}
                  </Text>
                </>
              )}
              <Text color="black">{name}</Text>
              <Text color="black">{prettifyEmployeePosition(position)}</Text>
            </Fragment>
          ))}
        </div>

        <br />
        <br />

        <Text strong color="black" className={styles.sectionTitle}>
          {locale.productDetails}
        </Text>

        <ViewRow
          label={locale.productType}
          value={deliveryLog?.productType}
          formatter={prettifyProductType}
          loading={loading}
        />
      </div>
      <DataTableV2
        {...table}
        size="small"
        hidePagination
        hideNoSearchResultRender
        tableVersion="v2"
        striped={true}
        className={styles.tableContainerView}
      />
    </>
  );
};

export default ViewDeliveryLog;
